<template>
  <div :class="['status-message', directionClass, statusClass, $attrs.class]">
    <p :class="['status-message__message', messageClass]">
      <slot name="message">
        {{ message }}
      </slot>
    </p>
    <div
      v-if="$slots.default"
      :class="['status-message__items', itemsClass]"
    >
      <slot />
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { semanticColors } from '@/constants/colors';

const props = defineProps({
  color: {
    type: String,
    default: 'neutral',
    validator: (value) => semanticColors.includes(value),
  },
  message: {
    type: String,
    required: true,
  },
  direction: {
    type: String,
    default: 'horizontal',
    validator: (value) => ['horizontal', 'vertical'].includes(value),
  },
  messageClass: {
    type: String,
    default: '',
  },
  itemsClass: {
    type: String,
    default: '',
  },
});

const directionClass = computed(() => {
  return {
    horizontal: 'is-horizontal',
    vertical: 'is-vertical',
  }[props.direction];
});

const statusClass = computed(() => {
  return {
    error: 'is-error',
    warning: 'is-warning',
    success: 'is-success',
    info: 'is-info',
    neutral: 'is-neutral',
    primary: 'is-primary',
  }[props.color];
});
</script>

<style>
@reference '@/assets/main.css';

.status-message {
  --dot-color: var(--color-gray-500);
  @apply gap-1;

  &.is-error {
    --dot-color: var(--color-red-500);
  }

  &.is-warning {
    --dot-color: var(--color-yellow-500);
  }

  &.is-success {
    --dot-color: var(--color-green-500);
  }

  &.is-primary {
    --dot-color: var(--color-brand-500);
  }

  @apply relative flex pl-3.5;

  &::before {
    @apply absolute content-[''] left-0 top-1.75 size-1.5 rounded-full bg-[var(--dot-color)];
  }
}

.status-message.is-horizontal {
  @apply flex-row;
}

.status-message.is-vertical {
  @apply flex-col;
}

.status-message__message {
  @apply font-medium text-sm;
}

.status-message__items {
  @apply text-sm text-gray-600;
}
</style>
